import { CheckIcon } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import type { CorridaDelAnalisis } from "@/lib/generacion";

interface ProgresoDelAnalisisProps {
  corrida: CorridaDelAnalisis;
}

/**
 * El análisis corriendo: paso real, porcentaje y contadores, todo leído de la
 * corrida (el servidor avanza por hitos, así que el porcentaje salta).
 *
 * No hay tiempo estimado: el servidor no lo conoce y uno inventado sería una
 * promesa. Lo que sí se dice es que se puede cerrar la página, que es verdad
 * porque el análisis corre en la cola.
 */
export function ProgresoDelAnalisis({ corrida }: ProgresoDelAnalisisProps) {
  const { paso, porcentaje, contadores, pasos } = corrida;

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(17.5rem,1fr)]">
      <Card variant="marcada" className="gap-4 rounded-[1.25rem] px-[1.375rem] py-5">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-ink text-base font-extrabold tracking-[-0.01em]">Analizando el expediente</span>
          <span className="text-ink font-mono text-[0.8125rem] font-bold">{porcentaje} %</span>
        </div>

        <Progress value={porcentaje} aria-label="Avance del análisis" />

        {paso ? (
          <p className="text-ink-secundario flex items-center gap-2 text-[0.78125rem] font-semibold" aria-live="polite">
            <span aria-hidden className="bg-primary size-2 shrink-0 animate-pulse rounded-full" />
            <span>
              {paso.nombre} <span className="text-ink-medio font-mono">· paso {paso.numero} de {paso.de}</span>
            </span>
          </p>
        ) : null}

        {pasos.length > 0 ? (
          <ul className="flex flex-col gap-1.5">
            {pasos.map((p) => {
              const terminado = p.estado === "ok";
              const actual = !terminado && paso?.nombre === p.nombre;

              return (
                <li
                  key={p.nombre}
                  className={cn("flex items-center gap-[0.5625rem] text-[0.78125rem] font-semibold", terminado ? "text-ink-secundario" : actual ? "text-ink" : "text-ink-hint")}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px]",
                      terminado
                        ? "border-primary bg-primary text-primary-foreground"
                        : actual
                          ? "border-primary"
                          : "border-input",
                    )}
                  >
                    {terminado ? <CheckIcon className="size-2.5" strokeWidth={4} /> : null}
                  </span>
                  {p.nombre}
                </li>
              );
            })}
          </ul>
        ) : null}
      </Card>

      <Card variant="marcada" className="bg-superficie-tenue gap-3.5 rounded-[1.25rem] px-[1.375rem] py-5">
        <span className="rotulo-seccion">Lo que va encontrando</span>
        <div className="grid grid-cols-2 gap-3">
          <Contador valor={contadores.hechos} rotulo="hechos con su fuente" />
          <Contador valor={contadores.dudas} rotulo="dudas levantadas" />
        </div>
        <p className="text-ink-secundario text-[0.78125rem] leading-normal font-semibold text-pretty">
          Puedes cerrar esta página. El análisis sigue corriendo; al volver verás las dudas listas para responder.
        </p>
        <p className="text-ink-medio font-mono text-xs font-semibold">
          {contadores.documentos} documentos · {contadores.llamadas_al_modelo}{" "}
          {contadores.llamadas_al_modelo === 1 ? "llamada" : "llamadas"} a la IA
        </p>
      </Card>
    </div>
  );
}

function Contador({ valor, rotulo }: { valor: number; rotulo: string }) {
  return (
    <div className="bg-card border-border rounded-[0.875rem] border-[1.5px] px-3.5 py-3">
      <div className="text-ink font-mono text-2xl font-bold">{valor}</div>
      <div className="text-ink-muted text-xs font-semibold">{rotulo}</div>
    </div>
  );
}
